import { useState, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { useImageUpload } from "@/hooks/useImageUpload";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, X, Image as ImageIcon, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import ImageComponent from "@/components/ui/image";
import { CameraDialog } from "@/components/images/camera-dialog";

export type EntityType = "product" | "company" | "user" | "generic";

interface ImageUploadProps {
  entityType: EntityType;
  entityId?: Id<"products" | "companies" | "users">;
  currentImageId?: Id<"_storage"> | undefined;
  onImageChange?: (imageId: Id<"_storage"> | undefined) => void;
  disabled?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  maxSizeKB?: number;
  aspectRatio?: number;
  quality?: number;
  uploadText?: string;
  dragText?: string;
  selectText?: string;
  className?: string;
}

export default function ImageUpload({
  entityType,
  entityId,
  currentImageId,
  onImageChange,
  disabled = false,
  maxWidth = 600,
  maxHeight = 600,
  maxSizeKB = 200,
  aspectRatio = 1,
  quality = 0.8,
  uploadText = "Subir imagen",
  dragText = "Suelta la imagen aquí",
  selectText = "Seleccionar imagen",
  className = "",
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const getImageDimensions = (
    file: File,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    });
  };

  const { uploadImage, isUploading, uploadProgress } = useImageUpload({
    maxWidth,
    maxHeight,
    quality,
    maxSizeKB,
    aspectRatio: imageDimensions
      ? imageDimensions.width / imageDimensions.height
      : aspectRatio,
  });

  // Mutations para diferentes tipos de entidades
  const updateProductImage = useMutation(
    api.companies.configuration.images.mutations.updateProductImage,
  );
  const updateCompany = useMutation(api.companies.mutations.updateCompany);
  const removeCompanyLogo = useMutation(
    api.companies.mutations.removeCompanyLogo,
  );
  const updateUserProfile = useMutation(
    api.system.users.mutations.updateProfile,
  );

  const currentImageUrl = useQuery(
    api.companies.configuration.images.queries.getImageUrl,
    currentImageId ? { storageId: currentImageId } : "skip",
  );

  const handleFileSelect = async (file: File) => {
    if (disabled || !file) return;

    // Verificar que sea una imagen
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecciona un archivo de imagen válido");
      return;
    }

    // Limpiar dimensiones anteriores
    setImageDimensions(null);

    // Procesar la imagen
    await handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    try {
      // Obtener dimensiones reales de la imagen
      const dimensions = await getImageDimensions(file);
      setImageDimensions(dimensions);

      // Subir la imagen con las dimensiones calculadas
      const storageId = await uploadImage(file);
      if (!storageId) return;

      // Notificar al componente padre
      onImageChange?.(storageId as Id<"_storage">);

      // Actualizar en la base de datos según el tipo de entidad
      if (entityId) {
        if (entityType === "product") {
          await updateProductImage({
            productId: entityId as Id<"products">,
            imageId: storageId as Id<"_storage">,
          });
        } else if (entityType === "company") {
          await updateCompany({
            id: entityId as Id<"companies">,
            logoId: storageId as Id<"_storage">,
          });
        } else if (entityType === "user") {
          await updateUserProfile({
            id: entityId as Id<"users">,
            image: storageId as Id<"_storage">,
          });
        }
        // Agregar más tipos según necesites
      }

      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    }
  };

  const handleRemoveImage = async () => {
    if (disabled) return;

    try {
      // Limpiar el estado de dimensiones
      setImageDimensions(null);

      // Notificar al componente padre
      onImageChange?.(undefined);

      // Actualizar en la base de datos según el tipo de entidad
      if (entityId) {
        if (entityType === "product") {
          await updateProductImage({
            productId: entityId as Id<"products">,
            imageId: undefined,
          });
        } else if (entityType === "company") {
          await removeCompanyLogo({
            id: entityId as Id<"companies">,
          });
        } else if (entityType === "user") {
          await updateUserProfile({
            id: entityId as Id<"users">,
            image: undefined,
          });
        }
        // Agregar más tipos según necesites
      }

      // Limpiar el input de archivo para permitir volver a cargar la misma imagen
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      toast.success("Imagen eliminada");
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Error al eliminar la imagen");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (imageFile) {
      handleFileSelect(imageFile);
    } else {
      toast.error("Por favor arrastra un archivo de imagen válido");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const getAspectRatioClass = (ratio: number) => {
    // Para relaciones de aspecto exactas conocidas
    if (Math.abs(ratio - 1) < 0.01) return "aspect-square";
    if (Math.abs(ratio - 16 / 9) < 0.01) return "aspect-video"; // 16:9
    if (Math.abs(ratio - 4 / 3) < 0.01) return "aspect-[4/3]"; // 4:3
    if (Math.abs(ratio - 3 / 4) < 0.01) return "aspect-[3/4]"; // 3:4
    if (Math.abs(ratio - 21 / 9) < 0.01) return "aspect-[21/9]"; // 21:9

    // Para relaciones personalizadas, generar clase dinámica
    // Redondear a 3 decimales para mayor precisión
    const roundedRatio = Math.round(ratio * 1000) / 1000;

    // Si es muy cercano a relaciones estándar conocidas, usar esas
    if (Math.abs(roundedRatio - 16 / 9) < 0.05) return "aspect-video"; // ~16:9
    if (Math.abs(roundedRatio - 4 / 3) < 0.05) return "aspect-[4/3]"; // ~4:3
    if (Math.abs(roundedRatio - 3 / 4) < 0.05) return "aspect-[3/4]"; // ~3:4

    // Para otras relaciones, generar clase personalizada
    // Usar una aproximación más precisa para generar relaciones de aspecto
    const findBestFraction = (ratio: number, maxDenom: number = 20) => {
      let bestNum = 1;
      let bestDenom = 1;
      let bestError = Math.abs(ratio - 1);

      for (let denom = 1; denom <= maxDenom; denom++) {
        const num = Math.round(ratio * denom);
        const error = Math.abs(ratio - num / denom);
        if (error < bestError && num > 0 && denom > 0) {
          bestNum = num;
          bestDenom = denom;
          bestError = error;
        }
      }

      return { num: bestNum, denom: bestDenom };
    };

    const { num, denom } = findBestFraction(roundedRatio);

    // Asegurar que tenemos valores válidos
    if (num <= 0 || denom <= 0) {
      return "aspect-square"; // fallback
    }

    return `aspect-[${num}/${denom}]`;
  };

  const getEntityText = () => {
    switch (entityType) {
      case "product":
        return "producto";
      case "company":
        return "empresa";
      case "user":
        return "usuario";
      case "generic":
        return "elemento";
      default:
        return "elemento";
    }
  };

  const getSizeText = (file?: File) => {
    if (file) {
      const sizeInKB = Math.round(file.size / 1024);
      const sizeText =
        sizeInKB > 1024
          ? `${(sizeInKB / 1024).toFixed(1)} MB`
          : `${sizeInKB} KB`;
      return `${sizeText} • ${maxSizeKB} KB máx.`;
    }
    return `Máximo ${maxSizeKB} KB`;
  };

  return (
    <div className={className}>
      <Card
        className={`relative transition-colors w-full flex items-center justify-center ${
          currentImageUrl
            ? "border-0 shadow-none p-0"
            : dragOver
              ? "border-blue-500 bg-blue-50 min-h-[120px]"
              : "border-dashed border-muted-foreground/30 min-h-[120px]"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <CardContent className={currentImageUrl ? "p-0 w-full" : "p-3"}>
          {currentImageUrl ? (
            <div className="relative w-full">
              <div
                className="relative w-full overflow-hidden rounded"
                style={{ aspectRatio: String(aspectRatio) }}
              >
                <ImageComponent
                  src={currentImageUrl}
                  alt={`Imagen de ${getEntityText()}`}
                  fill
                  className="object-cover"
                />
              </div>
              {!disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ) : (
            <div
              className={`flex flex-col items-center justify-center text-gray-500 ${getAspectRatioClass(aspectRatio)}`}
            >
              {isUploading ? (
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <div className="w-full max-w-xs">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-sm">
                    Subiendo imagen... {uploadProgress}%
                  </p>
                </div>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 mb-2" />
                  <p className="text-sm font-medium text-center">
                    {dragOver ? dragText : uploadText}
                  </p>
                  <p className="text-xs text-gray-400 text-center">
                    Clic para seleccionar
                  </p>
                  <div className="text-xs text-gray-400 mt-1 text-center">
                    <p>{getSizeText()}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {!currentImageUrl && !isUploading && (
        <div className="flex gap-2 mt-2 justify-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="h-8 w-8"
          >
            <Upload className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setCameraOpen(true)}
            disabled={disabled}
            className="h-8 w-8"
          >
            <Camera className="h-4 w-4" />
          </Button>
        </div>
      )}

      <CameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          setCameraOpen(false);
          handleFileSelect(file);
        }}
      />
    </div>
  );
}
